import { createContext, useContext, useEffect, useState } from "react";

type ThemeProviderProps = {
  children: React.ReactNode;
};

type ThemeProviderState = {
  theme: "dark" | "light";
};

const initialState: ThemeProviderState = {
  theme: "light",
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);
const darkModeMediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  const [colorScheme, setColorScheme] = useState<"dark" | "light">(() => {
    if (darkModeMediaQuery.matches) {
      window.document.documentElement.classList.add("dark");
      return "dark";
    } else {
      window.document.documentElement.classList.add("light");
      return "light";
    }
  });

  const handleColorSchemeChange = (event: any) => {
    window.document.documentElement.classList.remove("light", "dark");
    if (event.matches) {
      window.document.documentElement.classList.add("dark");
      setColorScheme("dark");
    } else {
      window.document.documentElement.classList.add("light");
      setColorScheme("light");
    }
  };

  useEffect(() => {
    darkModeMediaQuery.addEventListener("change", handleColorSchemeChange);
    return () => {
      darkModeMediaQuery.removeEventListener("change", handleColorSchemeChange);
    };
  }, []);

  const value = {
    theme: colorScheme,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
